import { useMemo } from 'react';
import { Alert } from '@vnetwork/ui/feedback';
import { type VendorReadState, useCdnAspectVendors, useDomainDetail } from '@/api/manticore';
import { cdnProviderLabel } from '../cdn-provider-display';

/**
 * The set of provider codenames this domain is actually bound to (lowercased).
 * Reuses the already-cached domain-detail query, so no extra request. Used to
 * scope drift warnings to real bindings — the fan-out `vendors` map can also
 * carry family providers this domain was never bound to (they reject with a
 * permission/"service not found" error), which must NOT read as drift.
 */
export function useDomainBindingProviders(domainId: string | undefined): Set<string> {
  const { data: domain } = useDomainDetail(domainId);
  return useMemo(
    () => new Set((domain?.bindings ?? []).map((b) => b.provider?.toLowerCase()).filter(Boolean) as string[]),
    [domain],
  );
}

/**
 * Warns when secondary (non-core) CDN providers failed to apply an aspect that
 * the core (vEdge) binding DID apply. Multi-CDN secondaries are best-effort by
 * design, so this never blocks — it just stops the failure from being swallowed,
 * which would make the config look synced everywhere. Renders nothing when:
 *  - the core aspect isn't configured (nothing to be out of sync with), or
 *  - every real binding is healthy.
 * A vendor only counts as drift when it is an actual binding of this domain
 * (see useDomainBindingProviders) — fan-out artifacts for unbound providers are
 * ignored.
 */
export function VendorDrift({
  vendors,
  coreProvider,
  coreConfigured,
  bindingProviders,
}: {
  vendors?: Record<string, VendorReadState>;
  coreProvider?: string;
  coreConfigured?: boolean;
  bindingProviders?: Set<string>;
}) {
  if (!vendors) return null;
  // No core config → secondary read errors aren't drift, just "nothing set".
  if (coreConfigured === false) return null;

  const core = (coreProvider ?? '').toLowerCase();
  const failed = Object.entries(vendors).filter(([name, st]) => {
    const n = name.toLowerCase();
    if (n === core || st.status !== 'error') return false;
    // Scope to real bindings when we know them; unbound fan-out artifacts drop out.
    if (bindingProviders && !bindingProviders.has(n)) return false;
    return true;
  });
  if (failed.length === 0) return null;

  const plural = failed.length > 1;
  return (
    <Alert tone="warning" title={`${failed.length} secondary CDN provider${plural ? 's are' : ' is'} out of sync`}>
      The primary provider applied this configuration, but the provider{plural ? 's' : ''} below did not.
      Secondary providers are best-effort and may be temporarily unavailable — saving again retries them.
      <ul style={{ margin: 'var(--vn-space-2) 0 0', paddingLeft: 'var(--vn-space-5)' }}>
        {failed.map(([name, st]) => (
          <li key={name}>
            {cdnProviderLabel(name)}
            {st.error ? `: ${st.error}` : ''}
          </li>
        ))}
      </ul>
    </Alert>
  );
}

/**
 * Self-fetching drift banner for a domain aspect. Reads per-vendor outcomes under
 * its own cache key (no clash with the tab's config read) and scopes drift to the
 * domain's real bindings via the already-cached domain detail. A config tab only
 * has to drop `<CdnAspectDrift domainId={domainId} path="<aspect>" />` by its form.
 */
export function CdnAspectDrift({ domainId, path }: { domainId: string; path: string }) {
  const { data } = useCdnAspectVendors(domainId, path);
  const bindingProviders = useDomainBindingProviders(domainId);
  return (
    <VendorDrift
      vendors={data?.vendors}
      coreProvider={data?.coreProvider}
      coreConfigured={data?.coreConfigured}
      bindingProviders={bindingProviders}
    />
  );
}
